import React, { useState, useEffect, useCallback } from 'react';
import { useTournament } from 'context/TournamentContext';
import { LANDING } from 'constants/routes';
import { WalletDataProvider } from 'context/WalletDataContext';
import { ReactComponent as BackIcon } from 'assets/Icon/angle-left-solid.svg';
import { MobileView } from 'react-device-detect';

import IconSvg from 'components/ui/IconSvg';
import FullPageLoad from 'components/ui/FullPageLoad';

import MobileBottomSection from 'components/StatusFlow/MobileBottomSection';
import BottomSection from 'components/StatusFlow/BottomSection';

import StatusWrapper from 'components/layout/StatusWrapper';
import TournamentRanking from 'components/game/TournamentRanking';

import JoinWrapper from 'components/layout/JoinWrapper';
import EndWrapper from 'components/layout/EndWrapper';

import EndPage from 'components/End/EndPage';
import JoinPage from 'components/Join/JoinPage';
import PaymentStatusOverlay from 'components/StatusTournament/PaymentStatusOverlay';
import TournamentJoinPayment from 'components/TournamentJoinPayment';
import { useTournamentJoinPayment } from 'components/TournamentJoinPayment/useTournamentJoinPayment';

import {
  Wrapper,
  ColLeft,
  ColRight,
  Box,
  RowTop,
  BtnBack,
} from './styled';

const StatusTournament = () => {
  const { 
    tournamentDetails, 
    tournamentStatus,
    // Payment fields from Task 19
    userPaymentStatus,
    isPaidTournament,
    isPaymentRequired,
    entryFee,
    checkUserPaymentStatus,
  } = useTournament();
  const [isloadPage, setLoadPage] = useState<Promise<boolean | void> | boolean>(true);

  // Extract tournamentId for hook
  const tournamentId = tournamentDetails?.data?.id?.toString() || '';

  // Task 40.1: Integrate useTournamentJoinPayment hook
  // Note: We pass onPaymentComplete inline to have access to paymentId from hook result
  const hookResult = useTournamentJoinPayment({
    tournamentId,
    onPaymentComplete: undefined, // Will be called manually
  });

  const {
    state: paymentState,
    handlePayAndJoin,
    error: paymentError,
    hasExistingSession,
    recoverySession,
    resumeSession,
    dismissSession,
    paymentId,
    stakeSession,
  } = hookResult;

  // Task 40.2: Callback to refresh payment status after payment completion
  // This is called when the hook's onPaymentComplete would fire
  const handlePaymentComplete = useCallback(async () => {
    // Refresh payment status in context
    // This will cause isPaymentRequired to become false and overlay to hide
    if (tournamentId && checkUserPaymentStatus && paymentId) {
      try {
        await checkUserPaymentStatus(tournamentId, paymentId);
      } catch (error) {
        // Log error but don't crash - the UI will still work
        console.error('[StatusTournament] Failed to refresh payment status:', error);
      }
    }
  }, [tournamentId, checkUserPaymentStatus, paymentId]);

  // Task 40.2: Trigger handlePaymentComplete when payment succeeds
  useEffect(() => {
    if (paymentState === 'success') {
      handlePaymentComplete();
    }
  }, [paymentState, handlePaymentComplete]);

  useEffect(() => {
    const timeout = setTimeout(() => setLoadPage(false), 1500)
    return () => clearTimeout(timeout);
  }, [])

  // Task 40.1: Determine if we should show overlay or payment form based on hook state
  const isPaymentFormActive = paymentState === 'loading' || paymentState === 'awaiting_payment' || paymentState === 'error';
  const isLoading = paymentState === 'loading';

  // Payment action handlers - now use hook methods
  const handleRetryPayment = () => {
    handlePayAndJoin();
  };

  const handleCreateNewPayment = () => {
    // Dismiss existing session first, then create new
    dismissSession();
    handlePayAndJoin();
  };

  const handleInitiatePayment = () => {
    handlePayAndJoin();
  };

  if (isloadPage) {
    return <FullPageLoad/>;
  };

  return (
    <StatusWrapper>
      <WalletDataProvider tournamentDetails={tournamentDetails}>
        <Wrapper>
          <BtnBack to={LANDING}>
            <IconSvg width={8} Icon={BackIcon} />BACK
          </BtnBack>

          <RowTop>
            <ColLeft>
              <Box style={{ position: 'relative' }}>
                {/* Task 40.3: Recovery banner for existing session */}
                {hasExistingSession && paymentState === 'idle' && recoverySession && (
                  <div data-testid="recovery-banner" style={{ marginBottom: '1rem' }}>
                    <p>You have an existing payment session. Resume or start fresh?</p>
                    <button onClick={resumeSession}>Resume</button>
                    <button onClick={() => { dismissSession(); handlePayAndJoin(); }}>Start New</button>
                  </div>
                )}
                {/* Payment status overlay for paid tournaments - show when idle and no payment form active */}
                {isPaidTournament && isPaymentRequired && paymentState === 'idle' && !hasExistingSession && (
                  <PaymentStatusOverlay
                    paymentStatus={userPaymentStatus}
                    isPaidTournament={isPaidTournament}
                    entryFee={entryFee}
                    onRetryPayment={handleRetryPayment}
                    onCreateNewPayment={handleCreateNewPayment}
                    onInitiatePayment={handleInitiatePayment}
                    stakeSession={stakeSession}
                  />
                )}
                {/* Inline TournamentJoinPayment - show when payment form is active */}
                {isPaymentFormActive && (
                  <TournamentJoinPayment
                    tournamentId={tournamentId}
                    stake={entryFee?.amount || '0'}
                    currency={entryFee?.currency || 'POL'}
                    onPayAndJoin={handlePayAndJoin}
                    isLoading={isLoading}
                    error={paymentError?.userMessage}
                  />
                )}
                {(tournamentStatus === 'FINISHED') ? (
                  <EndWrapper>
                    <EndPage tournamentDetails={tournamentDetails} />
                  </EndWrapper>
                ) :
                  <JoinWrapper>
                    <JoinPage tournamentDetails={tournamentDetails} />
                  </JoinWrapper>
                }
              </Box>
            </ColLeft>
            <ColRight>
              <Box>
                <TournamentRanking />
              </Box>
            </ColRight>
          </RowTop>

          <BottomSection tournamentDetails={tournamentDetails} />
        </Wrapper>

        <MobileView>
          <MobileBottomSection />
        </MobileView>
      </WalletDataProvider>
    </StatusWrapper>
  );
}
export default StatusTournament;
